import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { coins, certLookup, images, certificationServices } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fetchCertImages } from '../utils/certImages';

const BulkCertUpdateForm = ({ onComplete, selectedCoins = [], initialCertNumbers = '' }) => {
  const { user } = useAuth();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [fetchImages, setFetchImages] = useState(true);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'preview', 'updating'
  const [showAllCoins, setShowAllCoins] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [selectedFields, setSelectedFields] = useState({}); // Format: { coinId: { fieldName: boolean } }
  const [expandedCoins, setExpandedCoins] = useState({});
  const [coinsToUpdate, setCoinsToUpdate] = useState(selectedCoins);
  const [coinsWithCerts, setCoinsWithCerts] = useState([]);
  const [skippedCoins, setSkippedCoins] = useState([]);

  // Initialize and update coins state whenever props change
  React.useEffect(() => {
    if (Array.isArray(selectedCoins) && selectedCoins.length > 0) {
      setCoinsToUpdate(selectedCoins);
      // Filter coins with cert numbers and PCGS numbers
      const withCerts = selectedCoins.filter(coin => coin.certification_number);
      const withoutCerts = selectedCoins.filter(coin => !coin.certification_number);
      const withPcgsOnly = withoutCerts.filter(coin => coin.pcgs_number);
      const skipped = withoutCerts.filter(coin => !coin.pcgs_number);
      
      setCoinsWithCerts([...withCerts, ...withPcgsOnly]);
      setSkippedCoins(skipped);
    } else if (initialCertNumbers) {
      // If we have cert numbers but no selected coins, fetch the coins
      const fetchCoins = async () => {
        try {
          const certNumbers = initialCertNumbers.split('\n').filter(Boolean);
          const coinsData = await Promise.all(
            certNumbers.map(async (certNumber) => {
              try {
                const response = await coins.getByCertNumber(certNumber);
                return response;
              } catch (err) {
                console.error(`Failed to fetch coin with cert number ${certNumber}:`, err);
                return null;
              }
            })
          );
          const validCoins = coinsData.filter(Boolean);
          setCoinsToUpdate(validCoins);
          // Filter coins with cert numbers and PCGS numbers
          const withCerts = validCoins.filter(coin => coin.certification_number);
          const withoutCerts = validCoins.filter(coin => !coin.certification_number);
          const withPcgsOnly = withoutCerts.filter(coin => coin.pcgs_number);
          const skipped = withoutCerts.filter(coin => !coin.pcgs_number);
          
          setCoinsWithCerts([...withCerts, ...withPcgsOnly]);
          setSkippedCoins(skipped);
        } catch (err) {
          console.error('Failed to fetch coins:', err);
          setCoinsToUpdate([]);
        }
      };
      fetchCoins();
    } else {
      setCoinsToUpdate([]);
      setCoinsWithCerts([]);
      setSkippedCoins([]);
    }
  }, [selectedCoins, initialCertNumbers]);

  // Fetch column metadata on component mount
  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const columnData = await coins.getColumns();
        // Filter out hidden columns and system fields
        const relevantColumns = columnData
          .filter(col => !col.hidden)
          .filter(col => !['id', 'created_by', 'created_at', 'updated_at', 'certification_service_id', 'certification_number', 'status_id', 'purchase_date', 'purchase_price', 'shipping_cost', 'grading_cost', 'notes', 'provenance', 'user_coin_id', 'population_when_bought', 'pop_higher_when_bought', 'cac_sticker'].includes(col.column_name))
          .map(col => col.column_name);
        
        // Add certification_service_name and cac_sticker to columns
        setColumns(['certification_service_name', 'cac_sticker', ...relevantColumns]);
      } catch (err) {
        console.error('Failed to fetch columns:', err);
        // Fallback to essential fields if column fetch fails
        setColumns([
          'grade',
          'pcgs_number',
          'population',
          'pop_higher',
          'mintage',
          'country',
          'denomination',
          'year',
          'mint_mark',
          'variety',
          'cac_sticker'
        ]);
      }
    };
    fetchColumns();
  }, []);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  };

  const handlePreview = async () => {
    setLoading(true);
    setLogs([]);
    setPreview(null);

    try {
      // First, process coins with cert numbers
      const withCerts = coinsToUpdate.filter(coin => coin.certification_number);
      const withoutCerts = coinsToUpdate.filter(coin => !coin.certification_number);
      
      // Filter coins without cert numbers but with PCGS numbers
      const withPcgsOnly = withoutCerts.filter(coin => coin.pcgs_number);
      const skipped = withoutCerts.filter(coin => !coin.pcgs_number);
      
      setCoinsWithCerts(withCerts);
      setSkippedCoins(skipped);
      
      if (withCerts.length === 0 && withPcgsOnly.length === 0) {
        toast.error('No coins with certification numbers or PCGS numbers selected', { autoClose: 5000 });
        return;
      }

      addLog(`Found ${withCerts.length} coins with certification numbers`);
      if (withPcgsOnly.length > 0) {
        addLog(`Found ${withPcgsOnly.length} coins with PCGS numbers only`);
      }
      if (skipped.length > 0) {
        addLog(`Skipping ${skipped.length} coins without certification or PCGS numbers`, 'warn');
        skipped.forEach(coin => {
          addLog(`Skipped ${coin.user_coin_id} - No certification or PCGS number`, 'warn');
        });
      }

      const previewData = [];

      // Process coins with PCGS numbers but no cert numbers
      for (const existingCoin of withPcgsOnly) {
        try {
          const pcgsNumber = existingCoin.pcgs_number.trim();
          addLog(`Looking up PCGS number ${pcgsNumber} for ${existingCoin.user_coin_id}`);
          
          try {
            addLog(`Looking up PCGS data for number ${pcgsNumber}...`);
              let certInfo;
              let retryCount = 0;
              const maxRetries = 2;

              while (retryCount < maxRetries) {
                try {
                  certInfo = await certLookup.lookup('pcgs', pcgsNumber, null, true);  // Set isPCGSNumber=true
                  
                  // Validate basic info is present
                  if (!certInfo?.country || !certInfo?.year) {
                    if (retryCount === 0) {
                      addLog(`Incomplete data received for ${pcgsNumber} (missing country/year), retrying...`, 'warn');
                      retryCount++;
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      continue;
                    } else {
                      addLog(`Failed to get complete data for ${pcgsNumber} after retry - skipping`, 'error');
                      certInfo = null;
                      break;
                    }
                  }
                  
                  // If we got here, the data is valid
                  break;
                  
                } catch (err) {
                  if (err.response?.status === 502 && retryCount === 0) {
                    addLog(`Got 502 error, retrying lookup for ${pcgsNumber}...`, 'warn');
                    retryCount++;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    continue;
                  }
                  throw err;
                }
              }
            
              if (!certInfo) {
                addLog(`No valid certification data found for PCGS number ${pcgsNumber}`, 'error');
                continue;
              }

            // Map PCGS data to our fields - include all fields except population, pop_higher, grade, and price_guide_value
            const coinCertInfo = {
              pcgs_number: pcgsNumber,
              denomination: certInfo.denomination,
              year: certInfo.year,
              mint_mark: certInfo.mint_mark,
              variety: certInfo.variety,
              mintage: certInfo.mintage,
              metal: certInfo.metal,
              composition: certInfo.composition,
              weight: certInfo.weight,
              diameter: certInfo.diameter,
              edge: certInfo.edge,
              country: certInfo.country || 'United States',  // Default for PCGS
              minor_variety: certInfo.minor_variety,
              die_variety: certInfo.die_variety,
              reference_number: certInfo.reference_number,
              assayer: certInfo.assayer,
              cac_sticker: typeof certInfo.cac_sticker === 'object' ? certInfo.cac_sticker?.type?.toLowerCase() : (certInfo.cac_sticker?.toLowerCase() || 'none')
            };
          
            previewData.push({
              coinId: existingCoin.id,
              userCoinId: existingCoin.user_coin_id,
              certNumber: pcgsNumber,
              service: '',  // Don't set service for PCGS number lookups
              current: {
                certification_service_name: existingCoin.certification_service_name,
                grade: existingCoin.grade,
                pcgs_number: existingCoin.pcgs_number,
                current_population: existingCoin.current_population,
                current_pop_higher: existingCoin.current_pop_higher,
                mintage: existingCoin.mintage,
                country: existingCoin.country,
                denomination: existingCoin.denomination,
                year: existingCoin.year,
                mint_mark: existingCoin.mint_mark,
                variety: existingCoin.variety,
                minor_variety: existingCoin.minor_variety,
                die_variety: existingCoin.die_variety,
                metal: existingCoin.metal,
                composition: existingCoin.composition,
                weight: existingCoin.weight,
                diameter: existingCoin.diameter,
                edge: existingCoin.edge,
                reference_number: existingCoin.reference_number,
                assayer: existingCoin.assayer,
                price_guide_value: existingCoin.price_guide_value,
                cac_sticker: existingCoin.cac_sticker || 'none'
              },
              new: coinCertInfo,
              certInfo: coinCertInfo
            });
          } catch (certErr) {
            addLog(`Error looking up PCGS number ${pcgsNumber}: ${certErr.message}`, 'error');
            continue;
          }
        } catch (err) {
          addLog(`Error processing coin: ${err.message}`, 'error');
          continue;
        }
      }

      // First fetch all cert data in parallel
      const startTime = new Date().toISOString();
      addLog(`Starting parallel cert lookups for ${withCerts.length} coins`);
      
      // Create lookup promises with individual error handling and logging
      const lookupPromises = withCerts.map((existingCoin, index) => {
        return new Promise(async (resolve) => {
          try {
            const certNumber = existingCoin.certification_number.trim();
            addLog(`[${index + 1}/${withCerts.length}] Starting lookup for ${certNumber}`);
            
            // Detect service from cert number format
            const isPcgs = /^\d+$/.test(certNumber);
            const isNgc = /^\d{7}-\d{3}$/.test(certNumber);
            const service = isPcgs ? 'pcgs' : isNgc ? 'ngc' : null;
            
            if (!service) {
              addLog(`[${index + 1}/${withCerts.length}] Unable to determine service for ${certNumber}`, 'error');
              resolve({ existingCoin, error: 'Unable to determine certification service' });
              return;
            }

            addLog(`[${index + 1}/${withCerts.length}] Detected service ${service.toUpperCase()} for ${certNumber}`);

            // Extract grade for NGC lookup
            let lookupGrade = existingCoin.grade;
            if (service.toLowerCase() === 'ngc') {
              if (lookupGrade?.toLowerCase().includes('details')) {
                lookupGrade = 'Details';
              } else if (lookupGrade) {
                const numericMatch = lookupGrade.match(/(\d+)/);
                if (numericMatch) {
                  lookupGrade = numericMatch[1];
                }
              }
            }

            try {
              addLog(`[${index + 1}/${withCerts.length}] Making API request for ${certNumber}`);
              let certInfo;
              let retryCount = 0;
              const maxRetries = 2;

              while (retryCount < maxRetries) {
                try {
                  certInfo = await certLookup.lookup(service, certNumber, lookupGrade);
                  
                  // Validate basic info is present
                  if (!certInfo?.country || !certInfo?.year) {
                    if (retryCount === 0) {
                      addLog(`[${index + 1}/${withCerts.length}] Incomplete data received for ${certNumber} (missing country/year), retrying...`, 'warn');
                      retryCount++;
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      continue;
                    } else {
                      addLog(`[${index + 1}/${withCerts.length}] Failed to get complete data for ${certNumber} after retry - skipping`, 'error');
                      certInfo = null;
                      break;
                    }
                  }
                  
                  // If we got here, the data is valid
                  break;
                  
                } catch (err) {
                  if (err.response?.status === 502 && retryCount === 0) {
                    addLog(`[${index + 1}/${withCerts.length}] Got 502 error, retrying lookup...`, 'warn');
                    retryCount++;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    continue;
                  }
                  throw err;
                }
              }

              if (!certInfo) {
                resolve({ existingCoin, error: 'Failed to get complete certification data' });
                return;
              }
              
              addLog(`[${index + 1}/${withCerts.length}] Successfully retrieved data for ${certNumber}`);
              resolve({ existingCoin, service, certInfo });
            } catch (err) {
              addLog(`[${index + 1}/${withCerts.length}] Error looking up ${certNumber}: ${err.message}`, 'error');
              resolve({ existingCoin, error: err.message });
            }
          } catch (err) {
            addLog(`[${index + 1}/${withCerts.length}] Unexpected error: ${err.message}`, 'error');
            resolve({ existingCoin, error: err.message });
          }
        });
      });

      // Wait for all lookups with timeout
      addLog(`Waiting for ${withCerts.length} lookups to complete...`);
      const lookupResults = await Promise.all(lookupPromises);
      addLog(`All lookups completed`);

      // Process results sequentially
      const validResults = [];
      for (let i = 0; i < lookupResults.length; i++) {
        const result = lookupResults[i];
        const { existingCoin, service, certInfo, error } = result;
        const certNumber = existingCoin.certification_number.trim();

        addLog(`[${i + 1}/${lookupResults.length}] Processing ${existingCoin.user_coin_id} (${certNumber})`);

        if (error) {
          addLog(`Error processing ${existingCoin.user_coin_id}: ${error}`, 'error');
          continue;
        }

        if (!certInfo) {
          addLog(`No certification data found for ${certNumber}`, 'error');
          continue;
        }

        addLog(`Successfully retrieved data for ${certNumber}`);

        // Create a fresh copy of certInfo without images and add the service name
        const { images, ...certInfoWithoutImages } = certInfo;
        const coinCertInfo = { 
          ...certInfoWithoutImages,
          certification_service_name: service.toUpperCase(),
          cac_sticker: typeof certInfo.cac_sticker === 'object' ? certInfo.cac_sticker?.type?.toLowerCase() : (certInfo.cac_sticker?.toLowerCase() || 'none')
        };
      
        validResults.push({
          coinId: existingCoin.id,
          userCoinId: existingCoin.user_coin_id,
          certNumber,
          service: service.toUpperCase(),
          current: {
            certification_service_name: existingCoin.certification_service_name,
            grade: existingCoin.grade,
            pcgs_number: existingCoin.pcgs_number,
            current_population: existingCoin.current_population,
            current_pop_higher: existingCoin.current_pop_higher,
            mintage: existingCoin.mintage,
            country: existingCoin.country,
            denomination: existingCoin.denomination,
            year: existingCoin.year,
            mint_mark: existingCoin.mint_mark,
            variety: existingCoin.variety,
            minor_variety: existingCoin.minor_variety,
            die_variety: existingCoin.die_variety,
            metal: existingCoin.metal,
            composition: existingCoin.composition,
            weight: existingCoin.weight,
            diameter: existingCoin.diameter,
            edge: existingCoin.edge,
            reference_number: existingCoin.reference_number,
            assayer: existingCoin.assayer,
            price_guide_value: existingCoin.price_guide_value,
            cac_sticker: existingCoin.cac_sticker || 'none'
          },
          new: coinCertInfo,
          certInfo: coinCertInfo
        });
      }
      const endTime = new Date().toISOString();
      const duration = (endTime - startTime) / 1000;
      
      addLog(`Parallel processing completed in ${duration.toFixed(1)} seconds`);
      addLog(`Successfully processed ${validResults.length} out of ${withCerts.length} coins`);
      if (validResults.length < withCerts.length) {
        addLog(`Failed to process ${withCerts.length - validResults.length} coins`, 'warn');
      }
      
      previewData.push(...validResults);

      if (previewData.length > 0) {
        // Initialize selected fields state with all changed fields selected by default for each coin
        const initialSelectedFields = {};
        previewData.forEach(item => {
          initialSelectedFields[item.coinId] = {};
          
          // Process all fields from columns array
          columns.forEach(field => {
            if (field === 'current_population') {
              if (isValueChanged(item.current.current_population, item.certInfo.population, field)) {
                initialSelectedFields[item.coinId][field] = true;
              }
            } else if (field === 'current_pop_higher') {
              if (isValueChanged(item.current.current_pop_higher, item.certInfo.pop_higher, field)) {
                initialSelectedFields[item.coinId][field] = true;
              }
            } else if (field === 'certification_service_name') {
              if (isValueChanged(item.current.certification_service_name, item.certInfo.certification_service_name, field)) {
                initialSelectedFields[item.coinId][field] = true;
              }
            } else {
              if (isValueChanged(item.current[field], item.certInfo[field], field)) {
                initialSelectedFields[item.coinId][field] = true;
              }
            }
          });
        });
        
        setSelectedFields(initialSelectedFields);
        setPreview(previewData);
        setStep('preview');
        addLog('Preview ready - please review the changes');
      } else {
        toast.error('No valid coins found to update', { autoClose: 5000 });
      }

    } catch (err) {
      toast.error('Failed to generate preview', { autoClose: 5000 });
      addLog(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

    const handleConfirm = async () => {
      if (!preview) return;
      
      setStep('updating');
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      const processedCoinIds = new Set(); // Track which coins we've already processed

      try {
        // Fetch all certification services once
        const services = await certificationServices.getAll();
        const serviceMap = new Map(services.map(s => [s.name.toLowerCase(), s.id]));

        // Process all coins in parallel
        const updateStartTime = new Date().toISOString();
        addLog(`Starting parallel updates for ${preview.length} coins`);
        let updateCount = 0;
        
        const updateResults = await Promise.all(preview.map(async (item) => {
          // Skip if we've already processed this coin
          if (processedCoinIds.has(item.coinId)) {
            return { success: false, error: 'Duplicate coin' };
          }
          processedCoinIds.add(item.coinId);
          
          try {
            updateCount++;
            addLog(`[${updateCount}/${preview.length}] Starting update for ${item.userCoinId}`);
            
            // Use stored cert info from preview
            const certInfo = item.certInfo;
            addLog(`Processing certification data for ${item.userCoinId}`);

            // Use the existing coin data from preview instead of fetching again
            const existingCoin = item.current;
            addLog(`Using cached coin data for ${item.userCoinId}`);

            // If images are enabled, fetch them after the main update
            let certImages = [];
            if (fetchImages) {
              try {
                addLog(`Starting image fetch for ${item.userCoinId}`);
                // Use the cached cert info we already have from the preview step
                certImages = await fetchCertImages(
                  item.certNumber,
                  item.service,
                  certInfo.grade?.match(/\d+/)?.[0],
                  item.certInfo // Use the cached cert info from preview
                );
                if (certImages && certImages.length > 0) {
                  addLog(`Found ${certImages.length} certification images for ${item.certNumber}`);
                }
              } catch (imgErr) {
                addLog(`Failed to fetch images for ${item.certNumber}: ${imgErr.message}`, 'warn');
                certImages = [];
              }
            }

            // Generate certFields only for selected fields
            addLog(`Building update fields for ${item.userCoinId}`);
            const fieldStartTime = new Date().toISOString();

            // Process all fields synchronously for better performance
            const certFields = {};
            
            // Helper function to convert empty strings to null and parse values
            const processField = (value, type = 'string') => {
              if (value === '' || value === null || value === undefined) return null;
              switch (type) {
                case 'float': return value ? parseFloat(value) : null;
                case 'int': return value ? parseInt(value) : null;
                case 'boolean': return value || false;
                case 'date': return value || null;
                default: return value;
              }
            };

            // Field type configurations
            const fieldTypes = {
              price_guide_value: 'float',
              weight: 'float',
              diameter: 'float',
              current_population: 'int',
              current_pop_higher: 'int',
              mintage: 'int'
            };

            // Process each field
            for (const [key, value] of Object.entries(certInfo)) {
              // Skip system fields
              if (['id', 'certification_number', 'certification_service_id',
                   'user_coin_id', 'created_by', 'created_at', 'updated_at',
                   'images', 'status', 'status_id', 'status_name', 'status_color',
                   'tags'].includes(key)) {
                continue;
              }

              // Process field based on type
              switch (key) {
                case 'population':
                  if (selectedFields[item.coinId]?.current_population && 
                      isValueChanged(existingCoin.current_population, value, 'current_population')) {
                    const processedValue = processField(value, 'int');
                    certFields.current_population = processedValue;
                    addLog(`Processing population: ${processedValue} for ${item.userCoinId}`);
                  }
                  break;

                case 'pop_higher':
                  if (selectedFields[item.coinId]?.current_pop_higher && 
                      isValueChanged(existingCoin.current_pop_higher, value, 'current_pop_higher')) {
                    const processedValue = processField(value, 'int');
                    certFields.current_pop_higher = processedValue;
                    addLog(`Processing pop higher: ${processedValue} for ${item.userCoinId}`);
                  }
                  break;

                case 'certification_service_name':
                  if (selectedFields[item.coinId]?.certification_service_name && 
                      isValueChanged(existingCoin.certification_service_name, item.service, 'certification_service_name')) {
                    const serviceId = serviceMap.get(item.service.toLowerCase());
                    if (serviceId) {
                      certFields.certification_service_id = serviceId;
                      addLog(`Processing certification service: ${item.service} for ${item.userCoinId}`);
                    }
                  }
                  break;

                case 'pcgs_number':
                  if (selectedFields[item.coinId]?.[key] && 
                      isValueChanged(existingCoin[key], value, key) && value) {
                    const processedValue = processField(value);
                    certFields.pcgs_number = processedValue;
                    addLog(`Processing PCGS number: ${processedValue} for ${item.userCoinId}`);
                  }
                  break;

                default:
                  if (selectedFields[item.coinId]?.[key] && 
                      isValueChanged(existingCoin[key], value, key)) {
                    const processedValue = processField(value, fieldTypes[key] || 'string');
                    certFields[key] = processedValue;
                    addLog(`Processing ${key}: ${processedValue} for ${item.userCoinId}`);
                  }
              }
            }
            // Process fields and prepare update
            const processedTime = new Date().toISOString();
            const processingDuration = (processedTime - fieldStartTime) / 1000;
            addLog(`Field processing completed in ${processingDuration.toFixed(1)}s for ${item.userCoinId}`);

            // Add images if available and fetch images is enabled
            if (fetchImages && certImages && certImages.length > 0) {
              certFields.images = certImages;
              certFields.image_source = item.service.toLowerCase();
              certFields.certification_number = item.certNumber;
              addLog(`Including ${certImages.length} images for ${item.certNumber}`);
            }

            // Check if we have any fields to update
            const updatableFields = Object.keys(certFields);
            if (updatableFields.length === 0) {
              addLog(`No fields to update for ${item.userCoinId}`, 'warn');
              return { success: true, skipped: true };
            }

            // Log what we're about to update
            addLog(`Updating fields: ${updatableFields.join(', ')} for ${item.userCoinId}`);

            // Perform database update
            const updateStart = new Date().toISOString();
            await coins.update(item.coinId, certFields);
            const updateDuration = (new Date().toISOString() - updateStart) / 1000;
            addLog(`Database update completed in ${updateDuration.toFixed(1)}s for ${item.userCoinId}`);
            return { success: true };
          } catch (err) {
            addLog(`Failed to process coin ${item.userCoinId}: ${err.message}`, 'error');
            return { success: false, error: err.message };
          }
        }));

        // Count successes, failures and skips
        successCount = updateResults.filter(r => r.success && !r.skipped).length;
        const skippedCount = updateResults.filter(r => r.success && r.skipped).length;
        errorCount = updateResults.filter(r => !r.success).length;
        
        const updateEndTime = new Date().toISOString();
        const updateDuration = (updateEndTime - updateStartTime) / 1000;
        addLog(`Update process completed in ${updateDuration.toFixed(1)} seconds`);

      addLog(`Completed: ${successCount} successful updates, ${skippedCount} skipped (no changes), ${errorCount} errors`);
      
      if (successCount > 0) {
        toast.success(`Updated ${successCount} ${successCount === 1 ? 'coin' : 'coins'}`, { autoClose: 8000 });
        if (onComplete) onComplete();
      }
      if (errorCount > 0) {
        toast.warn(`${errorCount} updates failed. Check logs for details.`, { autoClose: 8000 });
      }

    } catch (err) {
      toast.error('Failed to process updates', { autoClose: 5000 });
      addLog(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
      setStep('input');
      setPreview(null);
    }
  };

  // Format value for display
  const formatValue = (field, value, isNewValue = false, hasChanged = false) => {
    // Format the raw value
    let formattedValue;
    
    // Handle numeric fields (showing 0 for empty values)
    if (['population', 'pop_higher', 'current_population', 'current_pop_higher', 'mintage'].includes(field)) {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = Number(num).toLocaleString();
    }
    else if (['price_guide_value'].includes(field)) {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      const hasDecimals = num % 1 !== 0;
      formattedValue = `$${Number(num).toLocaleString(undefined, {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2
      })}`;
    }
    else if (field === 'weight') {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = `${Number(parseFloat(num).toFixed(4)).toString()}g`;
    }
    else if (field === 'diameter') {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = `${Number(parseFloat(num).toFixed(2)).toString()}mm`;
    }
    // Handle non-numeric fields (showing 'None' for empty values)
    else if (value === null || value === undefined || value === '') {
      formattedValue = 'None';
    }
    // Handle all other fields
    else {
      formattedValue = value.toString();
    }

    // Apply color coding
    if (!hasChanged) {
      return <span className="text-gray-500">{formattedValue}</span>;
    } else if (isNewValue) {
      if (value === null || value === undefined || value === '') {
        return <span className="text-red-600 italic">{formattedValue}</span>;
      }
      return <span className="text-green-600">{formattedValue}</span>;
    } else {
      return <span className="text-gray-900">{formattedValue}</span>;
    }
  };

  // Get human-readable field names
  // Helper function to check if values are effectively different
  const isValueChanged = (currentValue, newValue, field) => {
    // For certification service name, always consider it changed if different (case-insensitive)
    if (field === 'certification_service_name') {
      // If current value is empty/null and new value exists, consider it changed
      if (!currentValue || currentValue === '') {
        return newValue && newValue !== '';
      }
      // Otherwise compare case-insensitive
      return (currentValue || '').toLowerCase() !== (newValue || '').toLowerCase();
    }
    
    // For other fields, if both values are empty (null/undefined/empty string), they're equal
    if ((!currentValue || currentValue === '') && (!newValue || newValue === '')) {
      return false;
    }
    
    // For numeric fields, compare as numbers
    if (['population', 'pop_higher', 'current_population', 'current_pop_higher', 'mintage', 'weight', 'diameter', 'price_guide_value', 'composition'].includes(field)) {
      const current = parseFloat(currentValue) || 0;
      const next = parseFloat(newValue) || 0;
      return current !== next;
    }
    
    // For other fields, compare as strings
    // Ensure consistent string comparison by trimming whitespace
    return String(currentValue).trim() !== String(newValue).trim();
  };

  const getFieldLabel = (field) => {
    const labels = {
      variety: 'Variety',
      metal: 'Metal',
      certification_service_name: 'Certification Service',
      grade: 'Grade',
      mint_mark: 'Mint Mark',
      country: 'Country',
      denomination: 'Denomination',
      year: 'Year',
      edge: 'Edge',
      composition: 'Composition',
      weight: 'Weight',
      diameter: 'Diameter',
      mintage: 'Mintage',
      minor_variety: 'Minor Variety',
      die_variety: 'Die Variety',
      pcgs_number: 'PCGS Number',
      price_guide_value: 'Price Guide Value',
      current_population: 'Current Population',
      current_pop_higher: 'Current Pop Higher',
      cac_sticker: 'CAC Sticker'
    };
    return labels[field] || field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="w-full space-y-2 px-4 py-2">
      {skippedCoins?.length > 0 && (
        <p className="text-sm text-yellow-600">
          {skippedCoins.length} coins will be skipped (no certification number)
        </p>
      )}
      
      {step === 'input' && coinsToUpdate?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                  <h4 className="text-sm font-medium text-gray-900">Selected Coins</h4>
                  <span className="text-sm text-gray-500">
                    Showing {showAllCoins ? coinsToUpdate.length : Math.min(10, coinsToUpdate.length)} of {coinsToUpdate.length}
                  </span>
                </div>
                <ul className="divide-y divide-gray-200">
                  {(showAllCoins ? coinsToUpdate : coinsToUpdate.slice(0, 10)).map((coin, index) => (
                    <li key={coin.id} className="px-4 py-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 text-sm">
                            <span className="font-medium text-gray-900">{coin.user_coin_id}</span>
                            <span className="text-gray-500">({coin.certification_number})</span>
                            <span className="text-gray-500">{[coin.country, coin.year].filter(Boolean).join(' ')}</span>
                            {coin.mint_mark && <span className="text-gray-400">{coin.mint_mark}</span>}
                            {coin.denomination && <span className="text-gray-500">• {coin.denomination}</span>}
                            {coin.variety && <span className="text-gray-400">• {coin.variety}</span>}
                            {coin.grade && <span className="text-gray-500">• {coin.grade}</span>}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {coinsToUpdate.length > 10 && (
                  <div className="px-4 py-3 bg-gray-50 text-right">
                    <button
                      type="button"
                      onClick={() => setShowAllCoins(!showAllCoins)}
                      className="text-sm text-indigo-600 hover:text-indigo-900"
                    >
                      {showAllCoins ? 'Show Less' : `View ${coinsToUpdate.length - 10} More`}
                    </button>
                  </div>
                )}
              </div>
            )}

      <div className="mt-4 bg-gray-50 rounded p-2">
        <h4 className="text-sm font-medium text-gray-900 mb-1">Steps:</h4>
        <ol className="list-decimal list-inside space-y-1">
          <li className={`text-sm ${step === 'input' ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
            Click "Preview Changes" to fetch the latest data
          </li>
          <li className={`text-sm ${step === 'preview' ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
            Review the changes and select the ones you want to retain
          </li>
          <li className={`text-sm ${step === 'updating' ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
            Click "Confirm Updates" to apply the changes
          </li>
        </ol>
      </div>

      <div className="mt-5 space-y-4">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="fetchImages"
                name="fetchImages"
                checked={fetchImages}
                onChange={(e) => setFetchImages(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="fetchImages" className="ml-2 block text-sm text-gray-900">
                Fetch certification images when available
              </label>
            </div>

            <div className="flex gap-3">
              {step === 'input' && (
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={loading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                    loading
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                >
                  {loading ? 'Loading...' : 'Preview Changes'}
                </button>
              )}
              {step === 'preview' && (
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={loading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                    loading
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                >
                  {loading ? 'Updating...' : 'Confirm Updates'}
                </button>
              )}
            </div>
          </div>

          {preview && (
            <div className="mt-6">
              <div className="mb-4 flex items-center justify-end gap-4 px-4">
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => setShowUnchanged(!showUnchanged)}
                >
                  {showUnchanged ? 'Hide Unchanged Fields' : 'Show Unchanged Fields'}
                </button>
                <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="selectAllChanges"
                      name="selectAllChanges"
                      className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                      checked={preview.every(item => {
                      const changedFields = columns.filter(field => {
                        if (field === 'current_population') {
                          return isValueChanged(item.current.current_population, item.certInfo.population, field);
                        }
                        if (field === 'current_pop_higher') {
                          return isValueChanged(item.current.current_pop_higher, item.certInfo.pop_higher, field);
                        }
                        if (field === 'certification_service_name') {
                          return isValueChanged(item.current.certification_service_name, item.certInfo.certification_service_name, field);
                        }
                        return isValueChanged(item.current[field], item.certInfo[field], field);
                      });
                      return changedFields.every(field => selectedFields[item.coinId]?.[field]);
                    })}
                    onChange={() => {
                      const allSelected = preview.every(item => {
                      const changedFields = columns.filter(field => {
                        if (field === 'current_population') {
                          return isValueChanged(item.current.current_population, item.certInfo.population, field);
                        }
                        if (field === 'current_pop_higher') {
                          return isValueChanged(item.current.current_pop_higher, item.certInfo.pop_higher, field);
                        }
                        if (field === 'certification_service_name') {
                          return isValueChanged(item.current.certification_service_name, item.certInfo.certification_service_name, field);
                        }
                        return isValueChanged(item.current[field], item.certInfo[field], field);
                      });
                      return changedFields.every(field => selectedFields[item.coinId]?.[field]);
                      });

                      const newFields = {};
                      preview.forEach(item => {
                        newFields[item.coinId] = {};
                        columns.forEach(field => {
                          if (field === 'current_population') {
                            if (isValueChanged(item.current.current_population, item.certInfo.population, field)) {
                              newFields[item.coinId][field] = !allSelected;
                            }
                          } else if (field === 'current_pop_higher') {
                            if (isValueChanged(item.current.current_pop_higher, item.certInfo.pop_higher, field)) {
                              newFields[item.coinId][field] = !allSelected;
                            }
                          } else if (field === 'certification_service_name') {
                            if (isValueChanged(item.current.certification_service_name, item.certInfo.certification_service_name, field)) {
                              newFields[item.coinId][field] = !allSelected;
                            }
                          } else {
                            if (isValueChanged(item.current[field], item.certInfo[field], field)) {
                              newFields[item.coinId][field] = !allSelected;
                            }
                          }
                        });
                      });
                      setSelectedFields(newFields);
                    }}
                    />
                    <span className="ml-2 text-sm text-gray-700">Select All Changes</span>
                  </label>
              </div>

              <div className="mt-4 space-y-2 border border-gray-200 rounded-lg p-4 bg-white">
                {preview.map((item, itemIndex) => {
                  // Count changes for this coin
                  const changedFields = columns.filter(field => {
                    if (field === 'current_population') {
                      return isValueChanged(item.current.current_population, item.certInfo.population, field);
                    }
                    if (field === 'current_pop_higher') {
                      return isValueChanged(item.current.current_pop_higher, item.certInfo.pop_higher, field);
                    }
                    if (field === 'certification_service_name') {
                      return isValueChanged(item.current.certification_service_name, item.certInfo.certification_service_name, field);
                    }
                    return isValueChanged(item.current[field], item.certInfo[field], field);
                  });

                  const isExpanded = expandedCoins[item.coinId] || false;

                  return (
                    <div key={itemIndex} className="bg-white border rounded-lg overflow-hidden">
                      {/* Coin Summary Header */}
                      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-900">{item.userCoinId}</span>
                              <span className="ml-2 text-gray-500">({item.certNumber})</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{[item.current.country, item.current.year].filter(Boolean).join(' ')}</span>
                              {item.current.mint_mark && <span className="ml-1 text-gray-400">{item.current.mint_mark}</span>}
                              {item.current.denomination && <span className="ml-2 text-gray-500">• {item.current.denomination}</span>}
                              {item.current.variety && <span className="ml-2 text-gray-400">• {item.current.variety}</span>}
                              {item.current.grade && <span className="ml-2 text-gray-500">• {item.current.grade}</span>}
                            </div>
                            <div className="ml-auto">
                              <span className="text-indigo-600 font-medium">{changedFields.length} {changedFields.length === 1 ? 'change' : 'changes'}</span>
                              <span className="ml-2 text-gray-500">from {item.service}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 pl-6">
                          <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                id={`selectAll-${item.coinId}`}
                                name={`selectAll-${item.coinId}`}
                                className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                                checked={changedFields.every(field => selectedFields[item.coinId]?.[field])}
                              onChange={() => {
                                const allSelected = changedFields.every(field => selectedFields[item.coinId]?.[field]);
                                setSelectedFields(prev => ({
                                  ...prev,
                                  [item.coinId]: {
                                    ...prev[item.coinId],
                                    ...changedFields.reduce((acc, field) => ({
                                      ...acc,
                                      [field]: !allSelected
                                    }), {})
                                  }
                                }));
                              }}
                            />
                            <span className="ml-2 text-sm text-gray-700">Select All</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setExpandedCoins(prev => ({
                              ...prev,
                              [item.coinId]: !isExpanded
                            }))}
                            className="w-6 h-6 flex items-center justify-center border border-gray-200 rounded text-indigo-600 hover:text-indigo-900 hover:bg-gray-50"
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Changes Table */}
                      {isExpanded && (
                        <div className="border-t border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="w-[60px] px-4 py-2">
                                  <span className="sr-only">Select</span>
                                </th>
                                <th scope="col" className="w-[200px] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Field
                                </th>
                                <th scope="col" className="w-[calc(50%-130px)] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Current Value
                                </th>
                                <th scope="col" className="w-[calc(50%-130px)] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  New Value
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {columns.map((field, fieldIndex) => {
                                const currentValue = field === 'certification_service_name' ? 
                                                   item.current.certification_service_name :
                                                   item.current[field];
                                const newValue = field === 'certification_service_name' ? item.service :
                                               field === 'current_population' ? item.certInfo.population : 
                                               field === 'current_pop_higher' ? item.certInfo.pop_higher : 
                                               item.certInfo[field];
                                const hasChanged = isValueChanged(currentValue, newValue, field);
                                
                                // Skip unchanged fields if they're hidden
                                if (!hasChanged && !showUnchanged) return null;

                                return (
                                  <tr key={`${itemIndex}-${field}`} className={fieldIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="w-12 px-4 py-2 whitespace-nowrap">
                                      {hasChanged && (
                                        <input
                                          type="checkbox"
                                          id={`field-${item.coinId}-${field}`}
                                          name={`field-${item.coinId}-${field}`}
                                          className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                                          checked={selectedFields[item.coinId]?.[field] || false}
                                          onChange={() => {
                                            setSelectedFields(prev => ({
                                              ...prev,
                                              [item.coinId]: {
                                                ...prev[item.coinId],
                                                [field]: !prev[item.coinId]?.[field]
                                              }
                                            }));
                                          }}
                                        />
                                      )}
                                    </td>
                                    <td className="w-1/5 px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {getFieldLabel(field)}
                                    </td>
                                    <td className="w-2/5 px-4 py-2 whitespace-nowrap text-sm">
                                      {formatValue(field, field === 'certification_service_name' ? 
                                                       item.current.certification_service_name :
                                                       item.current[field], false, hasChanged)}
                                    </td>
                                    <td className="w-2/5 px-4 py-2 whitespace-nowrap text-sm">
                                      {formatValue(field, field === 'certification_service_name' ? item.service :
                                                       field === 'current_population' ? item.certInfo.population : 
                                                       field === 'current_pop_higher' ? item.certInfo.pop_higher : 
                                                       item.certInfo[field], true, hasChanged)}
                                    </td>
                                  </tr>
                                );
                              }).filter(Boolean)}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      {logs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Update Log</h3>
            
            <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-auto">
              {logs.map((log, index) => (
                <div 
                  key={index}
                  className={`text-sm mb-1 ${
                    log.type === 'error' 
                      ? 'text-red-600' 
                      : log.type === 'warn'
                      ? 'text-yellow-600'
                      : 'text-gray-600'
                  }`}
                >
                  <span className="text-gray-400">
                    [{new Date(log.timestamp).toLocaleTimeString()}]
                  </span>{' '}
                  {log.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkCertUpdateForm;
